import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, ExpandLess, ExpandMore } from '@mui/icons-material';

import { ControlBox } from '../../atoms/control-box';
import { ValueBox } from '../../molecules/value-box';
import { Group } from '../../organisms/group';
import { ServiceAccessInput } from '../../organisms/settings/service-access-input';
import { useDispatch, useSelector } from '../../store/utils';
import { variablesChange } from '../../store/actions';
import { AzRouter } from '../../../../core/src/shared/enums';

const DevAzRouters = () => {
  const [serviceActive, config, stats] = useSelector(
    ({ variables }) =>
      [
        variables.SERVICE_ACTIVE,
        variables.AZ_ROUTER_CONFIG,
        variables.AZ_ROUTER_STATS,
      ] as const
  );
  const dispatch = useDispatch();

  const [newName, setNewName] = useState('');
  const [newClientId, setNewClientId] = useState('');
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});

  const toggleExpanded = (id: string) =>
    setExpandedIds((prev) => ({ ...prev, [id]: !prev[id] }));

  const routerIds = Object.keys(config || {}).filter(
    (id) => config[id] != null
  );

  const clientIdValid =
    newClientId.trim() !== '' &&
    Number.isInteger(Number(newClientId)) &&
    Number(newClientId) > 0;

  const handleAdd = () => {
    if (!newName.trim() || !clientIdValid) return;
    const id = Math.random().toString(36).substring(2, 12);
    dispatch(
      variablesChange({
        AZ_ROUTER_CONFIG: {
          ...config,
          [id]: {
            type: AzRouter.V1,
            name: newName.trim(),
            clientId: Number(newClientId),
          },
        },
      })
    );
    setNewName('');
    setNewClientId('');
  };

  const handleRemove = (id: string) => {
    const remainingConfig = { ...config };
    delete remainingConfig[id];
    const remainingStats = { ...stats };
    delete remainingStats[id];
    dispatch(
      variablesChange({
        AZ_ROUTER_CONFIG: remainingConfig,
        AZ_ROUTER_STATS: remainingStats,
      })
    );
  };

  if (!serviceActive) {
    return (
      <Group
        layout="list"
        title={
          <FormattedMessage id="azRouters.title" defaultMessage="AZ routery" />
        }
      >
        <ServiceAccessInput />
      </Group>
    );
  }

  const renderStatusChip = (id: string) => {
    const routerStats = stats?.[id];
    const status = routerStats?.communicationStatus;

    if (!routerStats || status === 'starting') {
      return (
        <Chip
          size="small"
          color="default"
          label={
            <FormattedMessage
              id="azRouters.status.starting"
              defaultMessage="Spouští se"
            />
          }
        />
      );
    }

    if (routerStats.communicationOk || status === 'ok') {
      return (
        <Chip
          size="small"
          color="success"
          label={
            <FormattedMessage id="azRouters.status.ok" defaultMessage="OK" />
          }
        />
      );
    }

    return (
      <Chip
        size="small"
        color="error"
        label={
          <FormattedMessage
            id="azRouters.status.error"
            defaultMessage="Chyba"
          />
        }
      />
    );
  };

  return (
    <Group
      layout="list"
      title={
        <FormattedMessage id="azRouters.title" defaultMessage="AZ routery" />
      }
    >
      {routerIds.length === 0 && (
        <ValueBox
          type="row"
          title={
            <FormattedMessage
              id="azRouters.empty"
              defaultMessage="Nejsou nakonfigurovány žádné AZ routery"
            />
          }
          value=""
        />
      )}

      {routerIds.map((id) => {
        const routerConfig = config[id];
        if (!routerConfig) return null;
        const routerStats = stats?.[id];
        const isExpanded = Boolean(expandedIds[id]);
        return (
          <React.Fragment key={id}>
            <ControlBox
              title={routerConfig.name || id}
              fullWidth
              control={
                <Stack direction="row" spacing={2} alignItems="center">
                  {renderStatusChip(id)}
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    <FormattedMessage
                      id="azRouters.clientId.value"
                      defaultMessage="Modbus ID: {id}"
                      values={{ id: routerConfig.clientId }}
                    />
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    <FormattedMessage
                      id="azRouters.counts"
                      defaultMessage="OK {ok} / Chyb {err}"
                      values={{
                        ok: routerStats?.communicationSuccess ?? 0,
                        err: routerStats?.communicationErrors ?? 0,
                      }}
                    />
                  </Typography>
                  <IconButton
                    onClick={() => toggleExpanded(id)}
                    size="small"
                    aria-label="toggle details"
                  >
                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                  </IconButton>
                  <IconButton
                    color="error"
                    onClick={() => handleRemove(id)}
                    size="small"
                  >
                    <Delete />
                  </IconButton>
                </Stack>
              }
            />
            <Collapse in={isExpanded} unmountOnExit>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 2,
                  fontFamily: 'monospace',
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  overflow: 'auto',
                  maxHeight: 400,
                }}
              >
                {JSON.stringify(routerStats ?? {}, null, 2)}
              </Box>
            </Collapse>
          </React.Fragment>
        );
      })}

      <ControlBox
        title={
          <FormattedMessage
            id="azRouters.add"
            defaultMessage="Přidat nový AZ router"
          />
        }
        fullWidth
        control={
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              label={
                <FormattedMessage id="azRouters.name" defaultMessage="Název" />
              }
              size="small"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') handleAdd();
              }}
              sx={{ width: 220 }}
            />
            <TextField
              label={
                <FormattedMessage
                  id="azRouters.clientId"
                  defaultMessage="Modbus ID"
                />
              }
              size="small"
              type="number"
              value={newClientId}
              onChange={(e) => setNewClientId(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') handleAdd();
              }}
              sx={{ width: 120 }}
            />
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleAdd}
              disabled={!newName.trim() || !clientIdValid}
              size="small"
            >
              <FormattedMessage
                id="azRouters.addButton"
                defaultMessage="Přidat"
              />
            </Button>
          </Stack>
        }
      />
    </Group>
  );
};

export default DevAzRouters;
